import type { LlmGenerationStreamEvent } from "../../protocol"

export type LlmGenerationStreamStatus = "idle" | "streaming" | "error"

export interface LlmGenerationClientMessage {
  id: string
  role: "user" | "assistant" | "system"
  content: string
  timestamp: Date
  isStreaming?: boolean
}

export interface LlmGenerationStreamState<
  TMessage extends LlmGenerationClientMessage,
  TCustom,
> {
  messages: TMessage[]
  input: string
  status: LlmGenerationStreamStatus
  error: string | null
  thinkingDurations: Record<string, number>
  customState: TCustom
  activeUserMessageId: string | null
  streamingMessageId: string | null
  startedAt: number | null
}

interface LlmGenerationStreamStateOptions<
  TMessage extends LlmGenerationClientMessage,
  TCustom,
> {
  initialCustomState: TCustom
  createAssistantMessage: (id: string) => TMessage
  appendAssistantDelta: (message: TMessage, delta: string) => TMessage
  now?: () => number
}

export function createInitialLlmGenerationStreamState<
  TMessage extends LlmGenerationClientMessage,
  TCustom,
>(initialCustomState: TCustom): LlmGenerationStreamState<TMessage, TCustom> {
  return {
    messages: [],
    input: "",
    status: "idle",
    error: null,
    thinkingDurations: {},
    customState: initialCustomState,
    activeUserMessageId: null,
    streamingMessageId: null,
    startedAt: null,
  }
}

function finishStreamingMessage<TMessage extends LlmGenerationClientMessage>(
  messages: TMessage[],
  streamingMessageId: string | null,
): TMessage[] {
  if (!streamingMessageId) {
    return messages
  }

  return messages.map((message) =>
    message.id === streamingMessageId
      ? { ...message, isStreaming: false }
      : message,
  )
}

export function applyDefaultLlmGenerationStreamEvent<
  TMessage extends LlmGenerationClientMessage,
  TCustom,
>(
  state: LlmGenerationStreamState<TMessage, TCustom>,
  event: LlmGenerationStreamEvent,
  options: LlmGenerationStreamStateOptions<TMessage, TCustom>,
): LlmGenerationStreamState<TMessage, TCustom> {
  if (event.event === "delta" && typeof event.data === "string") {
    const delta = event.data
    const streamingMessageId =
      state.streamingMessageId ?? `assistant-${options.now?.() ?? Date.now()}`
    const hasStreamingMessage = state.messages.some(
      (message) => message.id === streamingMessageId,
    )
    const messages = hasStreamingMessage
      ? state.messages
      : [...state.messages, options.createAssistantMessage(streamingMessageId)]
    const nextMessages = messages.map((message) =>
      message.id === streamingMessageId
        ? options.appendAssistantDelta(message, delta)
        : message,
    )
    const duration =
      state.activeUserMessageId && state.startedAt
        ? Math.round(((options.now?.() ?? Date.now()) - state.startedAt) / 1000)
        : 0

    return {
      ...state,
      status: state.status === "idle" ? "streaming" : state.status,
      streamingMessageId,
      messages: nextMessages,
      thinkingDurations:
        duration > 0 && state.activeUserMessageId
          ? {
              ...state.thinkingDurations,
              [state.activeUserMessageId]: duration,
            }
          : state.thinkingDurations,
    }
  }

  if (event.event === "done") {
    return {
      ...state,
      status: "idle",
      activeUserMessageId: null,
      streamingMessageId: null,
      startedAt: null,
      messages: finishStreamingMessage(
        state.messages,
        state.streamingMessageId,
      ),
    }
  }

  if (event.event === "error") {
    const error =
      typeof event.data === "object" &&
      event.data !== null &&
      "message" in event.data &&
      typeof event.data.message === "string"
        ? event.data.message
        : "LLM generation failed"

    return {
      ...state,
      status: "error",
      error,
      activeUserMessageId: null,
      streamingMessageId: null,
      startedAt: null,
      messages: finishStreamingMessage(
        state.messages,
        state.streamingMessageId,
      ),
    }
  }

  return state
}

export function appendStreamChunkToBuffer(input: {
  buffer: string
  chunk: string
}): {
  buffer: string
  events: LlmGenerationStreamEvent[]
} {
  const combined = input.buffer + input.chunk
  const blocks = combined.split("\n\n")
  const buffer = blocks.pop() ?? ""
  const events: LlmGenerationStreamEvent[] = []

  for (const block of blocks) {
    let eventName = ""
    const dataLines: string[] = []

    for (const line of block.split("\n")) {
      if (line.startsWith("event: ")) {
        eventName = line.slice(7).trim()
        continue
      }

      if (line.startsWith("data: ")) {
        dataLines.push(line.slice(6))
      }
    }

    const data = dataLines.join("\n")
    if (!eventName || !data) {
      continue
    }

    try {
      events.push({
        event: eventName,
        data: JSON.parse(data) as unknown,
      })
    } catch {
      events.push({
        event: eventName,
        data,
      })
    }
  }

  return {
    buffer,
    events,
  }
}
